import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LangContext";
import client from "@/lib/api";
import { ArrowUpRight, Zap, Brain, ShieldAlert, Radar, ChevronRight, TrendingUp, Cpu, Wallet } from "lucide-react";

export default function Landing() {
  const { t } = useLang();
  const tier = (v) => { const l = t(`tier_${v}`); return l === `tier_${v}` ? "" : l; };
  const nav = useNavigate();
  const [trending, setTrending] = useState([]);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    client.get("/market/trending?chain=solana").then(r => setTrending((r.data || []).slice(0, 6))).catch(()=>{});
    client.get("/programs").then(r => setPrograms(r.data || [])).catch(()=>{});
  }, []);

  return (
    <div data-testid="landing-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#222]">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute inset-0 hero-radial pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 pt-24 pb-32 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#CCFF00]/30 bg-[#CCFF00]/5 text-[#CCFF00] text-[10px] font-mono uppercase tracking-widest mb-6">
              <span className="w-1.5 h-1.5 bg-[#CCFF00] animate-pulse" /> LIVE — Claude Sonnet 4.5 · DexScreener · PumpFun
            </div>
            <h1 className="font-display font-bold text-balance text-[44px] md:text-[72px] lg:text-[88px] leading-[0.95] tracking-tight max-w-5xl">
              {t("hero_title")}
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-white/60 font-body leading-relaxed">
              {t("hero_sub")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button data-testid="hero-start-button" onClick={() => nav("/register")} className="btn-acid flex items-center gap-2">
                {t("hero_cta")} <ArrowUpRight size={16} />
              </button>
              <button data-testid="hero-opps-button" onClick={() => document.getElementById("live-scanner")?.scrollIntoView({ behavior: "smooth" })} className="btn-ghost flex items-center gap-2">
                {t("hero_cta2") || "See Live Opportunities"} <ChevronRight size={16} />
              </button>
              <Link to="/performance" data-testid="hero-performance-link" className="btn-ghost flex items-center gap-2 !border-[#CCFF00]/40">
                <span className="w-1.5 h-1.5 bg-[#CCFF00] rounded-full animate-pulse" /> {t("hero_perf_link")} <ChevronRight size={16} />
              </Link>
            </div>
          </motion.div>

          {/* Stat cluster */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#222] border border-[#222]">
            {[
              { k: "AI Engine", v: "Claude 4.5", icon: Brain },
              { k: "Data Sources", v: "PumpFun + DEX", icon: Radar },
              { k: "Exchanges", v: "Binance + Phantom", icon: Wallet },
              { k: "Latency", v: "< 200ms", icon: Zap },
            ].map((s, i) => (
              <div key={i} className="bg-[#0A0A0A] p-6 flex items-start gap-4">
                <s.icon size={20} className="text-[#CCFF00] mt-1" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">{s.k}</div>
                  <div className="font-display text-lg mt-1">{s.v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE SCANNER PREVIEW */}
      <section id="live-scanner" className="border-b border-[#222]">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-mono">LIVE SCANNER</div>
              <h2 className="font-display text-3xl md:text-5xl font-bold mt-2">Real opportunities, right now.</h2>
            </div>
            <Link to="/register" data-testid="scanner-cta-link" className="hidden md:inline-flex btn-acid !py-2 !px-4 !text-xs">Full Access</Link>
          </div>

          <div className="surface overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-3 border-b border-[#222] text-[10px] uppercase tracking-widest text-white/40 font-mono">
              <div className="col-span-3">Token</div>
              <div className="col-span-2 text-right">Price USD</div>
              <div className="col-span-2 text-right">Mkt Cap</div>
              <div className="col-span-2 text-right">Vol 24h</div>
              <div className="col-span-1 text-right">24h %</div>
              <div className="col-span-2 text-right">Potential</div>
            </div>
            {trending.length === 0 && (
              <div className="p-8 text-center text-white/40 font-mono text-sm">Loading live market data…</div>
            )}
            {trending.map((t) => (
              <div key={t.address} className="grid grid-cols-12 px-4 py-3 border-b border-[#222] last:border-0 hover:bg-[#141414] transition-colors items-center">
                <div className="col-span-3">
                  <div className="font-display text-sm font-semibold">{t.symbol}</div>
                  <div className="text-[10px] text-white/40 font-mono truncate">{t.name}</div>
                </div>
                <div className="col-span-2 text-right font-mono text-sm">${Number(t.price_usd).toFixed(6)}</div>
                <div className="col-span-2 text-right font-mono text-sm text-white/70">${Math.round(t.market_cap).toLocaleString()}</div>
                <div className="col-span-2 text-right font-mono text-sm text-white/70">${Math.round(t.volume_24h).toLocaleString()}</div>
                <div className={`col-span-1 text-right font-mono text-sm ${t.price_change_24h >= 0 ? "text-[#CCFF00]" : "text-[#FF3366]"}`}>
                  {t.price_change_24h >= 0 ? "+" : ""}{Number(t.price_change_24h).toFixed(1)}%
                </div>
                <div className="col-span-2 text-right">
                  <span className="inline-block px-2 py-1 bg-[#CCFF00]/10 text-[#CCFF00] text-[10px] font-mono">{tier(t.multiplier_potential)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 PROGRAMS */}
      <section id="programs" className="border-b border-[#222]">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-mono">PROGRAMS</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold mt-2 max-w-3xl text-balance">Choose your hunting ground.</h2>
          <p className="mt-4 max-w-2xl text-white/60">Three programs. Three risk profiles. One AI engine.</p>

          <div className="mt-12 grid md:grid-cols-3 gap-px bg-[#222] border border-[#222]">
            {programs.map((p, i) => {
              const isPremium = p.id === "pepite";
              const title = p.id === "basic" ? t("program_basic") : p.id === "memecoins" ? t("program_memecoins") : t("program_pepite");
              return (
                <div key={p.id} className={`bg-[#0A0A0A] p-8 relative ${isPremium ? "ring-1 ring-[#CCFF00]/30" : ""}`} data-testid={`program-card-${p.id}`}>
                  {isPremium && (
                    <div className="absolute -top-px right-4 bg-[#CCFF00] text-black text-[10px] font-mono uppercase tracking-widest px-2 py-1">Flagship</div>
                  )}
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono">Program {String(i + 1).padStart(2, "0")}</div>
                  <h3 className="font-display text-2xl font-bold mt-3">{title}</h3>
                  <p className="text-white/50 text-sm mt-3 leading-relaxed">{p.description}</p>

                  <div className="mt-8 border-t border-[#222] pt-6">
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-5xl font-bold">{Math.round(p.price_eur).toLocaleString()}</span>
                      <span className="text-white/40 font-mono text-sm">€</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono mt-2">{t("one_time")}</div>
                    <div className="mt-4 text-xs font-mono text-white/60">
                      {t("min_deposit")}: <span className="text-[#CCFF00]">{Math.round(p.min_deposit_eur).toLocaleString()}€</span>
                    </div>
                  </div>

                  <ul className="mt-6 space-y-2 text-xs font-mono text-white/60">
                    <li className="flex items-center gap-2"><span className="w-1 h-1 bg-[#CCFF00]" /> {t("feature_ai")}</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 bg-[#CCFF00]" /> {t("feature_auto")}</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 bg-[#CCFF00]" /> {t("feature_realtime")}</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 bg-[#CCFF00]" /> {p.commission_pct}% fee on winning trades only</li>
                  </ul>

                  <button
                    onClick={() => nav("/register?program=" + p.id)}
                    data-testid={`program-subscribe-${p.id}`}
                    className={`mt-8 w-full ${isPremium ? "btn-acid" : "btn-ghost"}`}
                  >
                    {t("subscribe")}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-[#222]">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid md:grid-cols-2 gap-12">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-mono">HOW IT WORKS</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-2 text-balance">From signal to position in 200ms.</h2>
          </div>
          <div className="space-y-px bg-[#222] border border-[#222]">
            {[
              { i: "01", icon: Radar, k: "Continuous market scanning", v: "DexScreener + PumpFun feeds are polled in real-time. Every new token is scored." },
              { i: "02", icon: Cpu, k: "Algorithmic + AI dual layer", v: "Liquidity, momentum & age combined with Claude Sonnet 4.5 analysis." },
              { i: "03", icon: Zap, k: "Automated execution", v: "Connect your Binance API and Phantom wallet — the engine fires when signals trigger." },
              { i: "04", icon: TrendingUp, k: "Exit on AI confidence drop", v: "Positions auto-close when the AI signal flips to SELL or risk explodes." },
            ].map((s) => (
              <div key={s.i} className="bg-[#0A0A0A] p-6 flex gap-6">
                <div className="font-mono text-xs text-[#CCFF00]">{s.i}</div>
                <div>
                  <div className="font-display text-lg font-semibold flex items-center gap-2"><s.icon size={16} className="text-[#CCFF00]" /> {s.k}</div>
                  <div className="text-white/50 text-sm mt-2">{s.v}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RISK */}
      <section className="border-b border-[#222]">
        <div className="max-w-[1400px] mx-auto px-6 py-16">
          <div className="border border-[#FF3366]/40 bg-[#FF3366]/5 p-8 flex gap-6 items-start" data-testid="risk-disclaimer">
            <ShieldAlert size={28} className="text-[#FF3366] flex-shrink-0 mt-1" />
            <div>
              <div className="font-display text-xl font-bold text-[#FF3366]">{t("risk_title")}</div>
              <p className="mt-3 text-white/70 text-sm leading-relaxed max-w-3xl">{t("risk_body")}</p>
            </div>
          </div>
          <div className="mt-6">
            <a href="/compliance" data-testid="landing-compliance-link" className="text-[11px] font-mono text-white/50 hover:text-[#FF3366] underline underline-offset-4">
              {t("comp_link")} →
            </a>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 py-24 text-center">
          <h2 className="font-display text-4xl md:text-6xl font-bold text-balance max-w-3xl mx-auto">{t("cta_final") || "Ready to hunt exponential gains?"}</h2>
          <div className="mt-10">
            <button data-testid="footer-cta-button" onClick={() => nav("/register")} className="btn-acid !text-base !py-5 !px-10">
              {t("hero_cta")} →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
